'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import { usePathname } from 'next/navigation'
import GlobalHeader from '@/components/GlobalHeader'
import { BuilderStorageCleanup } from './BuilderStorageCleanup'
import { useAuthStore } from '@/stores/auth'
import {
  BuilderProvider,
  useOptionalBuilderContext,
} from '@/contexts/BuilderContext'

const MobileNavigation = dynamic(
  () => import('@/components/Navigation/MobileNavigation')
)
const MobileAppSettings = dynamic(
  () => import('@/components/GlobalHeader/MobileAppSettings')
)

interface LayoutWrapperProps {
  children: React.ReactNode
}

function isBuilderRoute(pathname?: string | null): boolean {
  return (
    pathname?.startsWith('/builder') === true ||
    pathname?.startsWith('/stationery-builder') === true
  )
}

function LayoutContent({ children }: LayoutWrapperProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isAppSettingsOpen, setIsAppSettingsOpen] = useState(false)
  const [shouldRenderMobileMenu, setShouldRenderMobileMenu] = useState(false)
  const [shouldRenderAppSettings, setShouldRenderAppSettings] = useState(false)
  const { isAuthenticated } = useAuthStore()
  const builderContext = useOptionalBuilderContext()
  const builderActions = builderContext?.actions
  const pathname = usePathname()

  const shouldRunBuilderCleanup = isBuilderRoute(pathname)

  const handleMobileMenuToggle = () => {
    setShouldRenderMobileMenu(true)
    setIsMobileMenuOpen(!isMobileMenuOpen)
    setIsAppSettingsOpen(false) // Close app settings when opening menu
  }

  const handleAppSettingsToggle = () => {
    setShouldRenderAppSettings(true)
    setIsAppSettingsOpen(!isAppSettingsOpen)
    setIsMobileMenuOpen(false) // Close menu when opening app settings
  }

  return (
    <>
      {shouldRunBuilderCleanup && <BuilderStorageCleanup />}

      <GlobalHeader
        onMobileMenuClick={isAuthenticated ? handleMobileMenuToggle : undefined}
        onMobileMenuClose={() => setIsMobileMenuOpen(false)}
        isMobileMenuOpen={isMobileMenuOpen}
        onAppSettingsClick={handleAppSettingsToggle}
        isAppSettingsOpen={isAppSettingsOpen}
        isAuthenticated={isAuthenticated}
        builderActions={builderActions || undefined}
      />

      {isAuthenticated && shouldRenderMobileMenu && (
        <MobileNavigation
          isOpen={isMobileMenuOpen}
          onClose={() => setIsMobileMenuOpen(false)}
        />
      )}

      {shouldRenderAppSettings && (
        <MobileAppSettings
          isOpen={isAppSettingsOpen}
          onClose={() => setIsAppSettingsOpen(false)}
        />
      )}

      {children}
    </>
  )
}

export default function LayoutWrapper({ children }: LayoutWrapperProps) {
  const pathname = usePathname()
  const shouldProvideBuilderContext = isBuilderRoute(pathname)

  // Only builder surfaces publish header actions through BuilderContext.
  // Keeping this provider off the rest of the app avoids a global client context
  // with no consumers on landing, auth, checkout, and dashboard pages.
  if (shouldProvideBuilderContext) {
    return (
      <BuilderProvider>
        <LayoutContent>{children}</LayoutContent>
      </BuilderProvider>
    )
  }

  return <LayoutContent>{children}</LayoutContent>
}
